/**
 * Página oculta que recebe convites de guia (do cliente) e respostas (do guia) via postMessage.
 * Fallback quando a API /api/reservas/guia-convite não está disponível.
 * Armazena em localStorage: naviex-guia-convites-pendentes
 */

import { useEffect } from 'react';

const STORE_KEY = 'naviex-guia-convites-pendentes';
const MSG_CONVITE = 'naviex-convite-guia';
const MSG_RESPONDER = 'naviex-guia-responder-convite';
const MSG_GET_CONVITES = 'naviex-get-convites';
const MSG_CONVITES_RESULT = 'naviex-convites-result';
const MSG_GET_STATUS = 'naviex-get-convite-status';
const MSG_STATUS_RESULT = 'naviex-convite-status-result';

interface ConviteReservaData {
  tipoReserva?: 'exclusive' | 'shared';
  hasTransport?: boolean;
  valor?: number;
  cidadePartida?: string;
  numPessoas?: number;
  destinos?: string[];
  horario?: string;
  inscritos?: number;
  vagas?: number;
}

interface StoredConvite {
  id: string;
  tripId: string;
  guideId: string;
  guideEmail: string;
  guideName: string;
  days: string[];
  destination?: string;
  startDate?: string;
  endDate?: string;
  status: 'pendente' | 'aceito' | 'recusado';
  createdAt: string;
  reservaData?: ConviteReservaData;
  clientEmail?: string;
}

function loadConvites(): StoredConvite[] {
  try {
    const raw = localStorage.getItem(STORE_KEY);
    return raw ? (JSON.parse(raw) as StoredConvite[]) : [];
  } catch {
    return [];
  }
}

function saveConvites(list: StoredConvite[]) {
  localStorage.setItem(STORE_KEY, JSON.stringify(list));
}

export default function GuiaConvitesStorePage() {
  useEffect(() => {
    const handler = (event: MessageEvent) => {
      const msg = event.data;
      if (!msg || typeof msg !== 'object') return;

      if (msg.type === MSG_CONVITE) {
        const convite: StoredConvite = {
          id: `conv-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
          tripId: msg.tripId ?? '',
          guideId: msg.guideId ?? '',
          guideEmail: (msg.guideEmail ?? '').toLowerCase().trim(),
          guideName: msg.guideName ?? '',
          days: Array.isArray(msg.days) ? msg.days : [],
          destination: msg.destination,
          startDate: msg.startDate,
          endDate: msg.endDate,
          status: 'pendente',
          createdAt: new Date().toISOString(),
          reservaData: msg.reservaData ?? undefined,
          clientEmail: msg.clientEmail ?? undefined,
        };
        if (convite.guideEmail && convite.tripId) {
          const list = loadConvites();
          list.push(convite);
          saveConvites(list);
        }
      } else if (msg.type === MSG_RESPONDER) {
        const { conviteId, aceitar } = msg;
        if (!conviteId) return;
        const list = loadConvites();
        const idx = list.findIndex((c) => c.id === conviteId);
        if (idx >= 0) {
          list[idx].status = aceitar ? 'aceito' : 'recusado';
          saveConvites(list);
        }
      } else if (msg.type === MSG_GET_CONVITES) {
        const email = (msg.email ?? '').toLowerCase().trim();
        const list = loadConvites().filter((c) => c.guideEmail === email && c.status === 'pendente');
        (event.source as Window)?.postMessage({ type: MSG_CONVITES_RESULT, convites: list }, '*');
      } else if (msg.type === MSG_GET_STATUS) {
        const tripId = msg.tripId ?? '';
        const list = loadConvites().filter((c) => c.tripId === tripId);
        const statusPorDia: Record<string, 'aceito' | 'recusado' | 'pendente'> = {};
        list.forEach((c) => {
          c.days.forEach((d) => {
            if (c.status === 'aceito' || c.status === 'recusado') statusPorDia[d] = c.status;
          });
        });
        (event.source as Window)?.postMessage({ type: MSG_STATUS_RESULT, statusPorDia }, '*');
      }
    };

    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []);

  return (
    <div className="p-4 text-xs text-gray-400">
      Relay de convites (admin)
    </div>
  );
}

export { MSG_CONVITE, MSG_RESPONDER, MSG_GET_CONVITES, MSG_CONVITES_RESULT, MSG_GET_STATUS, MSG_STATUS_RESULT, STORE_KEY };
